/**
 * @fileoverview usePersistedDisclosure — show/hide panel state that sticks.
 * @module utils/use-persisted-disclosure
 *
 * A `useState<boolean>` for collapse/expand chrome whose value is remembered
 * per user, across sessions and page loads. Customer feedback: practitioners
 * re-collapse the same toggles every time they reopen a view — this makes the
 * choice persist.
 *
 * Mirrors the read-in-initializer / write-in-effect localStorage pattern used
 * by `usePersistedState`, `usePersistedSet` and the theme switcher, with two
 * differences:
 *
 * - The key is scoped by the current user's id, so two accounts on the same
 *   browser never share panel state (`morgan.panel.<userId>.<id>`).
 * - Passing `id === undefined` opts out of persistence entirely (plain
 *   ephemeral `useState`), so a shared component can expose an optional
 *   `persistId` prop without forcing every call site to persist.
 *
 * The dashboard shell blocks rendering until `getCurrentUser` resolves (and the
 * query is cached thereafter), so the user id is available synchronously on the
 * first render of any panel beneath it — no key flip / lost value on mount.
 */

import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";

export function usePersistedDisclosure(
  id: string | undefined,
  defaultOpen: boolean,
): [boolean, Dispatch<SetStateAction<boolean>>] {
  const { data: user } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );
  const scope = user?._id ?? "anon";
  const storageKey = id ? `morgan.panel.${scope}.${id}` : null;

  const [open, setOpen] = useState<boolean>(() => {
    if (!storageKey || typeof window === "undefined") return defaultOpen;
    const stored = window.localStorage.getItem(storageKey);
    if (stored === null) return defaultOpen;
    try {
      return JSON.parse(stored) as boolean;
    } catch {
      // Corrupt value — fall back to the default.
      return defaultOpen;
    }
  });

  useEffect(() => {
    if (!storageKey || typeof window === "undefined") return;
    window.localStorage.setItem(storageKey, JSON.stringify(open));
  }, [storageKey, open]);

  return [open, setOpen];
}
